import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ApiFailure } from './api.ts';

/** Original line icons: 24px grid, 1.75 stroke, round caps — one consistent family. */
const PATHS: Record<string, string> = {
  overview: 'M4 13h6V4H4zM14 20h6v-9h-6zM4 20h6v-4H4zM14 7h6V4h-6z',
  screens: 'M3 5h18v11H3zM8 20h8M12 16v4',
  locations: 'M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21zM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  library: 'M4 5h16v14H4zM4 15l4.5-4.5 4 4 2.5-2.5L20 17M15.5 9.5h.01',
  studio: 'M4 20l4-1 11-11-3-3L5 16zM14 6l3 3',
  playlists: 'M4 6h12M4 12h12M4 18h8M18 15v6l4-3z',
  schedule: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4M8 14h3',
  publish: 'M12 16V4M7 9l5-5 5 5M5 20h14',
  campaigns: 'M4 10v4l12 5V5zM16 9a3 3 0 0 1 0 6M7 15l1 5h3l-1-4',
  music: 'M9 18V6l11-2v12M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM20 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0z',
  reports: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  integrations: 'M9 7V3M15 7V3M6 7h12v4a6 6 0 0 1-12 0zM12 17v4',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
  alerts: 'M12 3a6 6 0 0 0-6 6c0 6-3 8-3 8h18s-3-2-3-8a6 6 0 0 0-6-6zM10 21h4',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-4-4',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
  moon: 'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z',
  plus: 'M12 5v14M5 12h14',
  close: 'M6 6l12 12M18 6L6 18',
  menu: 'M4 7h16M4 12h16M4 17h16',
  upload: 'M12 16V4M7 9l5-5 5 5M4 20h16',
  rollback: 'M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5',
  check: 'M5 12l5 5 9-10',
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  list: 'M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01',
  trash: 'M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3',
  play: 'M7 4v16l13-8z',
  logout: 'M15 4h4v16h-4M10 17l5-5-5-5M15 12H3',
};

export function Icon({ name, label, size = 18 }: { name: keyof typeof PATHS | string; label?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"
      role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <path d={PATHS[name] ?? PATHS.overview} />
    </svg>
  );
}

const TONE: Record<string, string> = {
  connected: 'ok', active: 'ok', ready: 'ok', completed: 'ok', approved: 'ok', live: 'ok', ok: 'ok', resolved: 'ok', succeeded: 'ok',
  stale: 'warn', downloading: 'info', received: 'info', queued: 'info', partial: 'warn', processing: 'info', scanning: 'info', quarantined: 'info',
  uploading: 'info', pending_stage: 'info', staged: 'info', warning: 'warn', submitted: 'info', scheduled: 'info', acknowledged: 'warn', executing: 'info', degraded: 'warn',
  offline: 'danger', failed: 'danger', rejected: 'danger', blocked: 'danger', critical: 'danger', revoked: 'danger', expired: 'danger', open: 'danger',
};

/** Status always has a text label (never color alone). */
export function Status({ value, label }: { value: string | null | undefined; label?: string }) {
  const v = value ?? 'unknown';
  return <span className={`badge ${TONE[v] ?? ''}`}>{label ?? v.replace(/_/g, ' ')}</span>;
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty" role="status">
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ error, retry }: { error: ApiFailure; retry?: () => void }) {
  return (
    <div className="alert danger" role="alert">
      <strong>{error.status === 403 ? 'You don’t have access to this.' : error.status === 404 ? 'Not found.' : 'Something went wrong.'}</strong> {error.message}
      {error.correlationId && <span className="subtle"> · Reference {error.correlationId.slice(0, 8)}</span>}
      {retry && (
        <button className="btn btn-sm" style={{ marginLeft: 12 }} onClick={retry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function Loading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="stack" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 52 }} />
      ))}
    </div>
  );
}

/** Modal sheet with focus trap, Escape to close and focus restore. */
export function Sheet({ title, subtitle, onClose, children, footer, wide }: { title: string; subtitle?: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    el?.querySelector<HTMLElement>('input, select, textarea, button:not([data-close])')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab' && el) {
        const f = [...el.querySelectorAll<HTMLElement>('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((x) => !x.hasAttribute('disabled'));
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) {
          e.preventDefault();
          f[f.length - 1]!.focus();
        } else if (!e.shiftKey && document.activeElement === f[f.length - 1]) {
          e.preventDefault();
          f[0]!.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus();
    };
  }, [onClose]);
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`sheet${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby="sheet-title" ref={ref}>
        <div className="sheet-head">
          <div>
            <h2 id="sheet-title">{title}</h2>
            {subtitle && <p className="muted" style={{ marginTop: 4 }}>{subtitle}</p>}
          </div>
          <button className="btn btn-ghost icon-btn" data-close onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>
        <div className="sheet-body">{children}</div>
        {footer && <div className="sheet-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Inspector({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <aside className="inspector" aria-label={`${title} details`}>
      <div className="card-head">
        <h2 style={{ fontSize: 18 }}>{title}</h2>
        <button className="btn btn-ghost icon-btn" onClick={onClose} aria-label="Close details">
          <Icon name="close" />
        </button>
      </div>
      <div className="body">{children}</div>
    </aside>
  );
}

interface Toast {
  id: number;
  text: string;
  tone?: 'danger';
}
const ToastCtx = createContext<(text: string, tone?: 'danger') => void>(() => undefined);
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone?: 'danger') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast${t.tone ? ` ${t.tone}` : ''}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export interface OrgInfo {
  id: string;
  name: string;
  role: string;
  permissions: string[];
  locationScope: string[] | null;
}
export const OrgCtx = createContext<OrgInfo | null>(null);
export function useOrg(): OrgInfo {
  const o = useContext(OrgCtx);
  if (!o) throw new Error('No organization');
  return o;
}
export const can = (o: OrgInfo, p: string) => o.permissions.includes(p);
export const orgPath = (o: OrgInfo, p: string) => `/v1/orgs/${o.id}${p}`;
